import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CompactFilterBar, Input, Badge, Modal, Select, SearchableSelect, LoadingSpinner, Pagination } from '@/components/common';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '@/services/api';
import { ImportExportControls } from '@/components/admin/ImportExportControls';
import { useVehicleSegments } from '@/hooks/useVehicles';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store';
import { canManageVehicleMaster } from './masterDataAccess';
import { BulkActionBar, BulkSelectAllCheckbox, BulkSelectCheckbox, useBulkSelection } from '@/components/bulk';
import type { PaginatedResponse } from '@/types';

interface VehicleMake {
  id: string;
  name: string;
  /** All categories this make spans (backend `VehicleMake.categories`) —
   *  used to request the combined (union) segment vocabulary below (round-2
   *  Wave C, item C2) for makes that span more than one category. */
  categories?: string[];
}

interface VehicleModel {
  id: string;
  make: string;
  make_name: string;
  name: string;
  code: string;
  category: string;
  segment: string;
  /** Derived, read-only: every distinct seating capacity across this
   *  model's variants, e.g. "7, 8". Seating is a VehicleVariant field —
   *  a model does not have one value. */
  seating_capacity: string;
  is_active: boolean;
}

// Real VehicleModel.category values (backend `CATEGORY_CHOICES`) — NOT the 4
// canonical segment buckets (2w/4w/cv/other). The segments endpoint maps
// these down to a bucket server-side to pick the right segment vocabulary.
const CATEGORY_OPTIONS = [
  { value: '2w', label: 'Two Wheeler' },
  { value: '4w_private', label: 'Private Car' },
  { value: '4w_taxi', label: 'Taxi / Commercial Car' },
  { value: 'cv_gcv', label: 'Goods Carrying Vehicle' },
  { value: 'cv_pcv', label: 'Passenger Carrying Vehicle' },
  { value: 'cv_misc', label: 'Miscellaneous CV' },
];

// Status is a plain is_active boolean -- '' means "don't filter", so the
// value that goes on the wire is only ever 'true' or 'false' once chosen.
const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const initialForm = { make: '', name: '', code: '', category: '', segment: '' };

const itemsPerPage = 20;

export function VehicleModelsPage() {
  const queryClient = useQueryClient();
  // Read-only for everyone but the country admin -- which is exactly
  // where the server draws the line. See ./masterDataAccess.ts.
  const canManage = canManageVehicleMaster(useAuthStore().user?.role);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  // COUNTRY-10 list filters. Deliberately separate from `formData`, which is
  // the add/edit modal's state — the two happen to name the same fields and
  // sharing them would make filtering the list edit the open form.
  const [filterMake, setFilterMake] = useState('');
  const [filterSegment, setFilterSegment] = useState('');
  const [filterSeating, setFilterSeating] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState(initialForm);

  const { data, isLoading, isError, error, refetch } = useQuery<PaginatedResponse<VehicleModel>>({
    queryKey: ['vehicle-models', page, debouncedSearch, filterMake, filterSegment, filterSeating, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(itemsPerPage) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterMake) params.set('make', filterMake);
      if (filterSegment) params.set('segment', filterSegment);
      if (filterSeating) params.set('seating_capacity', filterSeating);
      if (filterStatus) params.set('is_active', filterStatus);
      const res = await api.get(`/vehicles/models/?${params.toString()}`);
      return res.data;
    },
  });
  const models = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const visibleIds = models.map((m) => m.id);
  const selection = useBulkSelection(visibleIds);
  const activeFilterCount = [filterMake, filterSegment, filterSeating, filterStatus].filter(Boolean).length;

  const { data: makes = [] } = useQuery<VehicleMake[]>({
    queryKey: ['vehicle-makes-lookup'],
    queryFn: async () => {
      // Unpaginated lookup endpoint returns a plain array of {id, name, code, categories}
      const res = await api.get<VehicleMake[]>('/vehicles/makes/lookup/');
      return res.data;
    },
  });

  // Segment vocabulary is the SELECTED CATEGORY's, and only that one.
  //
  // This used to request the de-duped union across every category the *make*
  // sells (round-2 Wave C, item C2), on the reasoning that a make spanning 2w
  // and 4w should offer both. That reasoning was wrong: the segment describes
  // the model in front of you, not the manufacturer's range, so a maker of
  // both goods and passenger carriers offered "Passenger Carrier" on a Goods
  // Carrying Vehicle. The category is the question; the make is not.
  //
  // Disabled (no fetch) until a category has been chosen.
  const segmentCategories = formData.category ? [formData.category] : [];
  const { data: segments = [], isFetching: segmentsLoading } = useVehicleSegments(segmentCategories);

  // The LIST filter needs every segment in use, not the subset valid for
  // whichever category the add/edit modal currently has selected — those are
  // two different questions and `segments` above answers the modal's one. The
  // endpoint takes canonical buckets, so ask for all of them and take the union.
  const { data: filterSegments = [] } = useVehicleSegments([
    '2w',
    '4w',
    'cv_gcv',
    'cv_pcv',
    'cv_misc',
    'other',
  ]);

  // If the currently-set segment isn't valid for the (possibly just-changed
  // or just-loaded, e.g. on edit) category, clear it rather than silently
  // submitting a stale/invalid value. Covers both a manual category switch
  // and preloading a legacy model whose stored segment predates the 6.2
  // vocabulary clear.
  useEffect(() => {
    if (!formData.category || !formData.segment || segmentsLoading) return;
    if (!segments.includes(formData.segment)) {
      setFormData((prev) => (prev.segment ? { ...prev, segment: '' } : prev));
    }
  }, [formData.category, formData.segment, segments, segmentsLoading]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['vehicle-models'] });
    queryClient.invalidateQueries({ queryKey: ['vehicle-models-lookup'] });
  };

  const buildPayload = (data: typeof initialForm) => ({ ...data });

  const createMutation = useMutation({
    mutationFn: async (data: typeof initialForm) => {
      if (editingId) {
        const res = await api.patch(`/vehicles/models/${editingId}/`, buildPayload(data));
        return res.data;
      }
      const res = await api.post('/vehicles/models/', buildPayload(data));
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success(editingId ? 'Vehicle model updated' : 'Vehicle model created');
      setShowModal(false);
      setEditingId(null);
      setFormData(initialForm);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/vehicles/models/${id}/`);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Vehicle model deleted');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleEdit = (model: VehicleModel) => {
    setEditingId(model.id);
    setFormData({
      make: model.make,
      name: model.name,
      code: model.code,
      category: model.category || '',
      // If this segment turns out to be invalid for the category (e.g. a
      // legacy value from before the 6.2 vocabulary clear), the effect above
      // blanks it out once the category's segment list has loaded.
      segment: model.segment || '',
    });
    setShowModal(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this vehicle model? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialForm);
  };

  const makeOptions = makes.map((m) => ({ value: m.id, label: m.name }));
  const segmentOptions = segments.map((s) => ({ value: s, label: s }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Vehicle Models</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage vehicle models</p>
        </div>
        {canManage && (
        <div className="flex items-center gap-2">
          <ImportExportControls apiEndpoint="/vehicles/models/" invalidateKeys={['vehicle-models', 'vehicle-models-lookup']} />
          <Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setShowModal(true)}>
            Add Model
          </Button>
        </div>
        )}
      </div>

      {/* Search + the four list filters on one strip (Queue #213 style: one
          line at the widest breakpoint, a Filter toggle below it). Seating is
          a plain number box rather than a dropdown: the values in use are
          whatever has been imported, and there is no endpoint that enumerates
          them -- a hardcoded list would quietly hide models with any other
          value. */}
      <CompactFilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Search models..."
        activeFilterCount={activeFilterCount}
        onClearFilters={() => {
          setFilterMake('');
          setFilterSegment('');
          setFilterSeating('');
          setFilterStatus('');
          setPage(1);
        }}
      >
        <Select
          id="vehicle-model-filter-make"
          label="Make"
          options={[{ value: '', label: 'All Makes' }, ...makeOptions]}
          value={filterMake}
          onChange={(e) => {
            setFilterMake(e.target.value);
            setPage(1);
          }}
        />
        <Select
          id="vehicle-model-filter-segment"
          label="Segment"
          options={[
            { value: '', label: 'All Segments' },
            ...filterSegments.map((s) => ({ value: s, label: s })),
          ]}
          value={filterSegment}
          onChange={(e) => {
            setFilterSegment(e.target.value);
            setPage(1);
          }}
        />
        <Input
          id="vehicle-model-filter-seating"
          label="Seating Capacity"
          type="number"
          min={1}
          placeholder="Any"
          value={filterSeating}
          onChange={(e) => {
            setFilterSeating(e.target.value);
            setPage(1);
          }}
        />
        <Select
          id="vehicle-model-filter-status"
          label="Status"
          options={statusOptions}
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
        />
      </CompactFilterBar>

      {canManage && (
        <BulkActionBar
          resource="vehicles/models"
          entityName="models"
          selectedIds={selection.selectedIds}
          onChanged={invalidate}
          onClear={selection.clear}
        />
      )}

      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          // A failed request is not "there are no vehicle models" -- those
          // are two different states and this platform has a habit of
          // confusing them.
          <div role="alert" className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">Could not load vehicle models</h3>
            <p className="mt-2 text-gray-500">{getErrorMessage(error)}</p>
            <Button className="mt-4" variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : models.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No vehicle models found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by adding a vehicle model.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {canManage && (
                      <th className="px-6 py-3 text-left">
                        <BulkSelectAllCheckbox
                          checked={selection.allVisibleSelected}
                          indeterminate={selection.someVisibleSelected}
                          onChange={selection.toggleAllVisible}
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Make</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Segment</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Seating</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {models.map((model) => (
                    <tr key={model.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      {canManage && (
                        <td className="px-6 py-4">
                          <BulkSelectCheckbox
                            checked={selection.isSelected(model.id)}
                            onChange={() => selection.toggle(model.id)}
                            label={`Select ${model.name}`}
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{model.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{model.code}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{model.make_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{model.segment || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{model.seating_capacity || '-'}</td>
                      <td className="px-6 py-4">
                        <Badge variant={model.is_active ? 'success' : 'warning'}>
                          {model.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          {canManage && (
                          <>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(model)}>
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(model.id)}>
                            <TrashIcon className="w-4 h-4 text-red-600" />
                          </Button>
                          </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal isOpen={showModal} onClose={handleCloseModal} title={editingId ? 'Edit Vehicle Model' : 'Add Vehicle Model'}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // Task 6: Make moved from a native <select required> (which
            // blocked submission by itself) to SearchableSelect, a button
            // with no native form participation -- required is now cosmetic
            // only. Without this check an empty Make would reach the API
            // and come back as a generic toast instead of being caught here.
            if (!formData.make) {
              toast.error('Make is required.');
              return;
            }
            createMutation.mutate(formData);
          }}
          className="space-y-4"
        >
          <SearchableSelect
            id="model-make"
            label="Make"
            options={makeOptions}
            value={formData.make}
            onChange={(value) => setFormData({ ...formData, make: value })}
            placeholder="Select a make"
            required
          />
          <Input
            label="Name"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Code"
            name="code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            required
          />
          <Select
            label="Vehicle Category"
            name="category"
            options={CATEGORY_OPTIONS}
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value, segment: '' })}
            placeholder="Select a category"
            required
          />
          <Select
            label="Segment"
            name="segment"
            options={segmentOptions}
            value={formData.segment}
            onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
            placeholder={formData.category ? 'Select a segment' : 'Select a category first'}
            disabled={!formData.category}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" isLoading={createMutation.isPending}>{editingId ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default VehicleModelsPage;
